import { requestTemplateSave } from 'adflow';
import {
  AdElementDataSourceSchema,
  AdElementSchema,
  DateTimeFormatSchema,
  Datasources,
  DiplayOptionsTeamAssetSchema,
  DisplayOptionsOddsFormatSchema,
  PenaltiesFormatSchema,
  type AdElement,
  type AdTemplateDisplayOptions,
} from '@adflow/types';

import { createInvocationId, logTemplateWebMCP } from '../logging';
import {
  isTemplateBuilderOperation,
  operationSupported,
  type TemplateBuilderOperation,
} from '../operations';
import { readWorkingRevision } from '../revision';
import { validateImageContentWrite } from '../sanitize';
import {
  SAVE_CONFIRMATION_INSTRUCTION,
  mcpToolResult,
  toolError,
  toolSuccess,
  type TemplateWebMCPToolResult,
} from '../responses';
import type { AdvertiserRecord, TemplateToolContext } from '../toolContext';

const WIDGET_LAYER_MESSAGE =
  'Widget layers cannot be added, removed or have their data changed with this tool.';

const SINGLE_CALL_OPERATIONS = new Set<string>([
  'set_business_entity',
  'add_data_source',
  'remove_data_source',
  'commit_working_copy',
]);

export const TEMPLATE_BUILDER_APPLY_TOOL = {
  name: 'template_builder_apply',
  description:
    'Change the open Template Builder working copy. Input is { operation, ...args } or { changes: [{ operation, ...args }, ...] } for several element edits in one call. set_business_entity, add_data_source, and remove_data_source stay single calls. set_business_entity loads the advertiser fonts when no shadow brand is set; read brand again before choosing a font. Returns { ok, data?, error? }. Success data includes operation and workingRevision. A changes call returns operation "changes" and applied. If one step fails, the working copy is restored and error details include index and operation. update_element_data merges into the current element data; omitted fields stay and null clears a field. Pick fontFamily and fontVariant from brand fonts variants; do not guess labels such as regular. apply_animation and group_elements return UNSUPPORTED and do not write. commit_working_copy is two steps. The first call opens Confirm Save and returns an instruction to wait; it does not write. Call it again with userConfirmed true only when the user explicitly asked to save, or with cancel true to close the dialog without saving. Args: { expectedWorkingRevision, userConfirmed?, cancel? }. Apply it on its own. Cancel returns saved false. A blocking warning returns INVALID_INPUT. A revision mismatch returns STALE_REVISION. Error code is UNAUTHORIZED, INVALID_INPUT, EDITOR_NOT_READY, STALE_REVISION, UNSUPPORTED or UNEXPECTED.',
  enabled: true,
  annotations: {
    title: 'Apply Template Builder',
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: false,
  },
  inputSchema: {
    type: 'object',
    properties: {
      operation: { type: 'string' },
      expectedWorkingRevision: { type: 'number' },
      userConfirmed: {
        type: 'boolean',
        description:
          'Set true only when the user explicitly asked to save. Omit it to open Confirm Save and wait.',
      },
      cancel: {
        type: 'boolean',
        description:
          'Set true to close Confirm Save without saving. Do not combine with userConfirmed.',
      },
    },
    required: ['operation'],
    additionalProperties: true,
  } as const,
  outputSchema: {
    type: 'object',
    properties: {
      ok: { type: 'boolean' },
      data: { type: 'object' },
      error: {
        type: 'object',
        properties: { code: { type: 'string' }, message: { type: 'string' } },
        required: ['code', 'message'],
      },
    },
    required: ['ok'],
  } as const,
};

type ApplyData = {
  operation: string;
  workingRevision: number;
  applied?: number;
  saved?: boolean;
  instruction?: string;
};

const DOCUMENT_SNAPSHOT_FIELDS = [
  'elements',
  'name',
  'bgColour',
  'dataSources',
  'displayOptions',
  'brand',
  'brandKit',
  'brandKitId',
  'importSourceJSON',
  'importSourceType',
  'customFonts',
  'businessEntityId',
  'selectedElementId',
  'workingRevision',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function advertiserRecordId(record: AdvertiserRecord): number | null {
  const id = record._id;
  if (typeof id === 'number' && Number.isFinite(id)) {
    return id;
  }
  if (typeof id === 'string' && id !== '') {
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

type DisplayOptionsKey = keyof AdTemplateDisplayOptions;

const ELEMENT_FORMAT_DISPLAY_KEY: Record<
  'set_element_date_format' | 'set_element_odds_format' | 'set_element_penalties_format',
  DisplayOptionsKey
> = {
  set_element_date_format: 'dateTimeFormat',
  set_element_odds_format: 'odds',
  set_element_penalties_format: 'penaltiesFormat',
};

function isDisplayOptionsKey(key: unknown): key is DisplayOptionsKey {
  return (
    key === 'teamAssets' || key === 'odds' || key === 'dateTimeFormat' || key === 'penaltiesFormat'
  );
}

const DISPLAY_OPTION_VALUE_SCHEMAS = {
  teamAssets: DiplayOptionsTeamAssetSchema,
  odds: DisplayOptionsOddsFormatSchema,
  dateTimeFormat: DateTimeFormatSchema,
  penaltiesFormat: PenaltiesFormatSchema,
} as const;

function parseTemplateDisplayOptionValue(key: DisplayOptionsKey, value: unknown) {
  return DISPLAY_OPTION_VALUE_SCHEMAS[key].safeParse(value);
}

function elementExists(state: Record<string, unknown>, elementId: string): boolean {
  return (
    Array.isArray(state.elements) &&
    state.elements.some((element) => isRecord(element) && element.id === elementId)
  );
}

const FONT_FAMILY_FIELDS = ['fontWeight', 'fontStyle', 'fontStretch', 'fontVariant'] as const;

function mergeElementData(
  current: unknown,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const base = isRecord(current) ? current : {};
  const next: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    const existing = base[key];
    if (isRecord(value) && isRecord(existing)) {
      next[key] = { ...existing, ...value };
      continue;
    }
    next[key] = value;
  }
  return next;
}

function fontFamilyPatchError(currentData: unknown, patch: Record<string, unknown>): string | null {
  if (!isRecord(patch.style) || !Object.prototype.hasOwnProperty.call(patch.style, 'fontFamily')) {
    return null;
  }
  const currentStyle =
    isRecord(currentData) && isRecord(currentData.style) ? currentData.style : {};
  if (patch.style.fontFamily === currentStyle.fontFamily) {
    return null;
  }
  const missing = FONT_FAMILY_FIELDS.filter(
    (field) => !Object.prototype.hasOwnProperty.call(patch.style, field),
  );
  if (missing.length === 0) {
    return null;
  }
  return `A fontFamily change must also include ${missing.join(', ')}.`;
}

function applied(
  operation: string,
  state: Record<string, unknown>,
): TemplateWebMCPToolResult<ApplyData> {
  return mcpToolResult(
    toolSuccess({
      operation,
      workingRevision: readWorkingRevision(state),
    }),
  );
}

async function runOperation(
  context: TemplateToolContext,
  operation: TemplateBuilderOperation,
  input: Record<string, unknown>,
): Promise<TemplateWebMCPToolResult<ApplyData>> {
  if (!operationSupported(operation)) {
    return mcpToolResult(
      toolError('UNSUPPORTED', `${operation} is not available on Template Builder WebMCP.`),
    );
  }

  const state = context.store?.getState();
  if (!context.store || !state || !Array.isArray(state.elements)) {
    return mcpToolResult(
      toolError('EDITOR_NOT_READY', 'The Template Builder editor is not ready.'),
    );
  }

  const actions = state as Record<string, unknown> & {
    setName: (name: string) => void;
    setBgColour: (colour: string) => void;
    setBusinessEntityId: (id: number) => void;
    setCustomFonts: (fonts: Array<{ fontFamily: string; src?: string }>) => void;
    setBrandKitId: (id: string | null) => void;
    setBrandKit: (brandKit: unknown) => void;
    setElementSelected: (id: string | null) => void;
    updateElementName: (id: string, name: string) => void;
    updateElementPosition: (id: string, x: number, y: number) => void;
    removeElement: (id: string) => void;
    addElement: (element: AdElement) => void;
    updateElementData: (id: string, data: AdElement['data']) => void;
    updateElementLocked: (id: string, locked: boolean) => void;
    updateElementHidden: (id: string, hidden: boolean) => void;
    updateElementDataSources: (
      id: string,
      sources: Array<{ sourceId: string; name: string }>,
    ) => void;
    removeElementDataSource: (id: string, dataSourceId: string) => void;
    addDataSource: (
      type: Datasources.SourceType,
      dataType?: Datasources.DataType,
      id?: string,
    ) => void;
    removeDataSource: (id: string) => void;
    setDisplayOptions: (key: string, value: unknown) => void;
    setElementDateDisplayOptions: (id: string, value: unknown) => void;
    setElementOddsDisplayOptions: (id: string, value: unknown) => void;
    setElementPenaltiesFormat: (id: string, value: unknown) => void;
  };

  const requireElement = (elementId: unknown): TemplateWebMCPToolResult<ApplyData> | null => {
    if (typeof elementId !== 'string' || !elementExists(state, elementId)) {
      return mcpToolResult(toolError('INVALID_INPUT', 'That element is not on the open template.'));
    }
    return null;
  };

  switch (operation) {
    case 'set_name': {
      if (typeof input.name !== 'string') {
        return mcpToolResult(toolError('INVALID_INPUT', 'set_name requires name.'));
      }
      actions.setName(input.name);
      return applied(operation, context.store.getState());
    }
    case 'set_background': {
      if (typeof input.colour !== 'string') {
        return mcpToolResult(toolError('INVALID_INPUT', 'set_background requires colour.'));
      }
      actions.setBgColour(input.colour);
      return applied(operation, context.store.getState());
    }
    case 'set_business_entity': {
      if (typeof input.businessEntityId !== 'number') {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'set_business_entity requires businessEntityId.'),
        );
      }
      const routeMode = context.routeMode ?? 'edit';
      if (routeMode === 'edit') {
        return mcpToolResult(
          toolError(
            'INVALID_INPUT',
            'set_business_entity is only available on create and duplicate.',
          ),
        );
      }
      if (!context.listAdvertisers) {
        return mcpToolResult(
          toolError('EDITOR_NOT_READY', 'The advertiser list is not available.'),
        );
      }
      const advertisers = await context.listAdvertisers();
      const businessEntityId = input.businessEntityId;
      const knownAdvertiser = advertisers.some(
        (row) => advertiserRecordId(row) === businessEntityId,
      );
      if (!knownAdvertiser) {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'set_business_entity received an unknown businessEntityId.'),
        );
      }
      if (state.brand == null && context.loadAdvertiserFonts) {
        try {
          const fonts = await context.loadAdvertiserFonts(businessEntityId);
          actions.setCustomFonts(fonts);
        } catch (error) {
          return mcpToolResult(
            toolError('UNEXPECTED', 'Advertiser fonts could not be loaded.', {
              message: error instanceof Error ? error.message : String(error),
            }),
          );
        }
      }
      actions.setBusinessEntityId(businessEntityId);
      return applied(operation, context.store.getState());
    }
    case 'set_brand_kit': {
      if (input.brandKitId !== null && typeof input.brandKitId !== 'string') {
        return mcpToolResult(toolError('INVALID_INPUT', 'set_brand_kit requires brandKitId.'));
      }
      actions.setBrandKitId(input.brandKitId);
      if ('brandKit' in input) {
        actions.setBrandKit(input.brandKit ?? null);
      }
      return applied(operation, context.store.getState());
    }
    case 'select_element': {
      if (input.elementId !== null && typeof input.elementId !== 'string') {
        return mcpToolResult(toolError('INVALID_INPUT', 'select_element requires elementId.'));
      }
      if (typeof input.elementId === 'string') {
        const missing = requireElement(input.elementId);
        if (missing) return missing;
      }
      actions.setElementSelected(input.elementId);
      return applied(operation, context.store.getState());
    }
    case 'rename_element': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (typeof input.name !== 'string') {
        return mcpToolResult(toolError('INVALID_INPUT', 'rename_element requires name.'));
      }
      actions.updateElementName(input.elementId as string, input.name);
      return applied(operation, context.store.getState());
    }
    case 'move_element': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (typeof input.x !== 'number' || typeof input.y !== 'number') {
        return mcpToolResult(toolError('INVALID_INPUT', 'move_element requires x and y.'));
      }
      actions.updateElementPosition(input.elementId as string, input.x, input.y);
      return applied(operation, context.store.getState());
    }
    case 'remove_element': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      const target = (state.elements as Array<{ id?: string; type?: string }>).find(
        (element) => element.id === input.elementId,
      );
      if (target?.type === 'WIDGET') {
        return mcpToolResult(toolError('INVALID_INPUT', WIDGET_LAYER_MESSAGE));
      }
      actions.removeElement(input.elementId as string);
      return applied(operation, context.store.getState());
    }
    case 'add_element': {
      const parsed = AdElementSchema.safeParse(input.element);
      if (!parsed.success) {
        return mcpToolResult(toolError('INVALID_INPUT', 'add_element requires a valid element.'));
      }
      if (parsed.data.type === 'WIDGET') {
        return mcpToolResult(toolError('INVALID_INPUT', WIDGET_LAYER_MESSAGE));
      }
      actions.addElement(parsed.data);
      return applied(operation, context.store.getState());
    }
    case 'update_element_data': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (!isRecord(input.data)) {
        return mcpToolResult(toolError('INVALID_INPUT', 'update_element_data requires data.'));
      }
      const current = (
        state.elements as Array<{ id?: string; type?: string; data?: unknown }>
      ).find((element) => element.id === input.elementId);
      if (current?.type === 'WIDGET') {
        return mcpToolResult(toolError('INVALID_INPUT', WIDGET_LAYER_MESSAGE));
      }
      const fontError = fontFamilyPatchError(current?.data, input.data);
      if (fontError) {
        return mcpToolResult(toolError('INVALID_INPUT', fontError));
      }
      if (
        current?.type === 'IMAGE' &&
        isRecord(input.data) &&
        Object.prototype.hasOwnProperty.call(input.data, 'content')
      ) {
        const imageContentError = validateImageContentWrite(input.data.content);
        if (imageContentError) {
          return mcpToolResult(toolError('INVALID_INPUT', imageContentError));
        }
      }
      const parsed = AdElementSchema.safeParse({
        ...current,
        data: mergeElementData(current?.data, input.data),
      });
      if (!parsed.success) {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'update_element_data received an invalid element.'),
        );
      }
      actions.updateElementData(input.elementId as string, parsed.data.data);
      return applied(operation, context.store.getState());
    }
    case 'set_element_locked': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (typeof input.locked !== 'boolean') {
        return mcpToolResult(toolError('INVALID_INPUT', 'set_element_locked requires locked.'));
      }
      actions.updateElementLocked(input.elementId as string, input.locked);
      return applied(operation, context.store.getState());
    }
    case 'set_element_hidden': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (typeof input.hidden !== 'boolean') {
        return mcpToolResult(toolError('INVALID_INPUT', 'set_element_hidden requires hidden.'));
      }
      actions.updateElementHidden(input.elementId as string, input.hidden);
      return applied(operation, context.store.getState());
    }
    case 'set_element_sources': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (!Array.isArray(input.sources)) {
        return mcpToolResult(toolError('INVALID_INPUT', 'set_element_sources requires sources.'));
      }
      const parsedSources = AdElementDataSourceSchema.array().safeParse(input.sources);
      if (!parsedSources.success) {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'set_element_sources received invalid sources.'),
        );
      }
      actions.updateElementDataSources(input.elementId as string, parsedSources.data);
      return applied(operation, context.store.getState());
    }
    case 'remove_element_source': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (typeof input.dataSourceId !== 'string') {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'remove_element_source requires dataSourceId.'),
        );
      }
      actions.removeElementDataSource(input.elementId as string, input.dataSourceId);
      return applied(operation, context.store.getState());
    }
    case 'add_data_source': {
      const parsedType = Datasources.SourceTypeSchema.safeParse(input.type);
      if (!parsedType.success) {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'add_data_source received an invalid type.'),
        );
      }
      let dataType: Datasources.DataType | undefined;
      if (input.dataType !== undefined && input.dataType !== null) {
        const parsedDataType = Datasources.DataTypeSchema.safeParse(input.dataType);
        if (!parsedDataType.success) {
          return mcpToolResult(
            toolError('INVALID_INPUT', 'add_data_source received an invalid dataType.'),
          );
        }
        dataType = parsedDataType.data;
      }
      if (input.id !== undefined && input.id !== null && typeof input.id !== 'string') {
        return mcpToolResult(toolError('INVALID_INPUT', 'add_data_source id must be a string.'));
      }
      const requestedId = typeof input.id === 'string' ? input.id : undefined;
      if (
        requestedId &&
        Array.isArray(state.dataSources) &&
        state.dataSources.some((source) => isRecord(source) && source.id === requestedId)
      ) {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'add_data_source id already exists on this template.'),
        );
      }
      actions.addDataSource(parsedType.data, dataType, requestedId);
      return applied(operation, context.store.getState());
    }
    case 'remove_data_source': {
      if (typeof input.dataSourceId !== 'string') {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'remove_data_source requires dataSourceId.'),
        );
      }
      actions.removeDataSource(input.dataSourceId);
      return applied(operation, context.store.getState());
    }
    case 'set_display_options': {
      if (!isDisplayOptionsKey(input.key)) {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'set_display_options requires a known key.'),
        );
      }
      const parsedDisplay = parseTemplateDisplayOptionValue(input.key, input.value);
      if (!parsedDisplay.success) {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'set_display_options received an invalid value.'),
        );
      }
      actions.setDisplayOptions(input.key, parsedDisplay.data);
      return applied(operation, context.store.getState());
    }
    case 'set_element_date_format':
    case 'set_element_odds_format':
    case 'set_element_penalties_format': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      const displayKey = ELEMENT_FORMAT_DISPLAY_KEY[operation];
      const parsed = parseTemplateDisplayOptionValue(displayKey, input.value);
      if (!parsed.success) {
        return mcpToolResult(toolError('INVALID_INPUT', `${operation} received an invalid value.`));
      }
      if (operation === 'set_element_date_format') {
        actions.setElementDateDisplayOptions(input.elementId as string, parsed.data);
      } else if (operation === 'set_element_odds_format') {
        actions.setElementOddsDisplayOptions(input.elementId as string, parsed.data);
      } else {
        actions.setElementPenaltiesFormat(input.elementId as string, parsed.data);
      }
      return applied(operation, context.store.getState());
    }
    case 'commit_working_copy': {
      if (
        typeof input.expectedWorkingRevision !== 'number' ||
        !Number.isFinite(input.expectedWorkingRevision)
      ) {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'commit_working_copy requires expectedWorkingRevision.'),
        );
      }
      if (input.userConfirmed != null && typeof input.userConfirmed !== 'boolean') {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'commit_working_copy userConfirmed must be a boolean.'),
        );
      }
      if (input.cancel != null && typeof input.cancel !== 'boolean') {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'commit_working_copy cancel must be a boolean.'),
        );
      }
      if (input.userConfirmed === true && input.cancel === true) {
        return mcpToolResult(
          toolError('INVALID_INPUT', 'commit_working_copy cannot confirm and cancel together.'),
        );
      }
      const settlement = await requestTemplateSave({
        expectedWorkingRevision: input.expectedWorkingRevision,
        userConfirmed: input.userConfirmed === true,
        cancel: input.cancel === true,
        signal: context.signal,
      });
      if (context.signal?.aborted) {
        return mcpToolResult(toolError('UNEXPECTED', 'The tool invocation was cancelled.'));
      }
      const revision = readWorkingRevision(context.store.getState());
      if (settlement.status === 'awaiting_confirmation') {
        return mcpToolResult(
          toolSuccess({
            operation,
            workingRevision: revision,
            saved: false,
            instruction: SAVE_CONFIRMATION_INSTRUCTION,
          }),
        );
      }
      if (settlement.status === 'saved') {
        return mcpToolResult(toolSuccess({ operation, workingRevision: revision, saved: true }));
      }
      if (settlement.status === 'cancelled') {
        return mcpToolResult(toolSuccess({ operation, workingRevision: revision, saved: false }));
      }
      if (settlement.status === 'blocked') {
        return mcpToolResult(toolError('INVALID_INPUT', settlement.message));
      }
      if (settlement.status === 'stale') {
        return mcpToolResult(
          toolError(
            'STALE_REVISION',
            'The working copy changed before save. Read the document again.',
          ),
        );
      }
      return mcpToolResult(
        toolError('UNEXPECTED', 'Template Builder save failed.', {
          message: settlement.message,
        }),
      );
    }
    default:
      return mcpToolResult(toolError('INVALID_INPUT', 'Unknown apply operation.'));
  }
}

function snapshotDocument(state: Record<string, unknown>): Record<string, unknown> {
  const snapshot: Record<string, unknown> = {};
  for (const field of DOCUMENT_SNAPSHOT_FIELDS) {
    snapshot[field] = state[field];
  }
  return structuredClone(snapshot);
}

function stepFailure(
  result: TemplateWebMCPToolResult<ApplyData>,
  index: number,
  operation: string,
): TemplateWebMCPToolResult<ApplyData> {
  if (result.structuredContent.ok) {
    return result;
  }
  const { code, message, details } = result.structuredContent.error;
  const prior = isRecord(details) ? details : {};
  const restored = message.includes('working copy was restored')
    ? message
    : `${message} The working copy was restored.`;
  return mcpToolResult(toolError(code, restored, { ...prior, index, operation }));
}

function parseChanges(
  input: Record<string, unknown>,
):
  | { changes: Array<Record<string, unknown> & { operation: TemplateBuilderOperation }> }
  | { error: string } {
  if (!Array.isArray(input.changes) || input.changes.length === 0) {
    return { error: 'changes must be a non-empty array.' };
  }
  const changes: Array<Record<string, unknown> & { operation: TemplateBuilderOperation }> = [];
  for (const [index, step] of input.changes.entries()) {
    if (!isRecord(step) || !isTemplateBuilderOperation(String(step.operation ?? ''))) {
      return { error: `changes[${index}] requires a known operation.` };
    }
    if (SINGLE_CALL_OPERATIONS.has(String(step.operation))) {
      return {
        error: `changes cannot include ${step.operation}. Apply it on its own.`,
      };
    }
    changes.push(step as Record<string, unknown> & { operation: TemplateBuilderOperation });
  }
  return { changes };
}

export async function executeTemplateBuilderApply(
  context: TemplateToolContext,
): Promise<TemplateWebMCPToolResult<ApplyData>> {
  const invocationId = createInvocationId();
  const startedAt = Date.now();
  const input = isRecord(context.input) ? context.input : undefined;
  const operation =
    typeof input?.operation === 'string'
      ? input.operation
      : input && 'changes' in input
      ? 'changes'
      : undefined;
  logTemplateWebMCP('invocation_start', {
    tool: TEMPLATE_BUILDER_APPLY_TOOL.name,
    invocationId,
    operation,
  });

  const finish = (
    result: TemplateWebMCPToolResult<ApplyData>,
    outcome: 'success' | 'validation_failure' | 'cancellation' | 'unexpected_failure',
  ) => {
    logTemplateWebMCP('invocation_complete', {
      tool: TEMPLATE_BUILDER_APPLY_TOOL.name,
      invocationId,
      operation,
      outcome,
      durationMs: Date.now() - startedAt,
      workingRevision: result.structuredContent.ok
        ? result.structuredContent.data.workingRevision
        : undefined,
    });
    return result;
  };

  try {
    if (context.signal?.aborted) {
      return finish(
        mcpToolResult(toolError('UNEXPECTED', 'The tool invocation was cancelled.')),
        'cancellation',
      );
    }
    if (!context.canEdit) {
      return finish(
        mcpToolResult(toolError('UNAUTHORIZED', 'Template Builder edit permission is required.')),
        'validation_failure',
      );
    }
    if (!input) {
      return finish(
        mcpToolResult(
          toolError('INVALID_INPUT', 'template_builder_apply requires a known operation.'),
        ),
        'validation_failure',
      );
    }
    if ('changes' in input) {
      if ('operation' in input) {
        return finish(
          mcpToolResult(
            toolError(
              'INVALID_INPUT',
              'template_builder_apply accepts operation or changes, not both.',
            ),
          ),
          'validation_failure',
        );
      }
      const parsed = parseChanges(input);
      if ('error' in parsed) {
        return finish(
          mcpToolResult(toolError('INVALID_INPUT', parsed.error)),
          'validation_failure',
        );
      }
      if (!context.store || typeof context.store.setState !== 'function') {
        return finish(
          mcpToolResult(
            toolError(
              'INVALID_INPUT',
              'A batch apply needs a store that can restore the working copy.',
            ),
          ),
          'validation_failure',
        );
      }
      const current = context.store.getState();
      if (!current || !Array.isArray(current.elements)) {
        return finish(
          mcpToolResult(toolError('EDITOR_NOT_READY', 'The Template Builder editor is not ready.')),
          'validation_failure',
        );
      }
      const snapshot = snapshotDocument(current);
      const restore = () => context.store?.setState?.(snapshot);
      for (const [index, step] of parsed.changes.entries()) {
        try {
          const result = await runOperation(context, step.operation, step);
          if (!result.structuredContent.ok) {
            restore();
            return finish(stepFailure(result, index, step.operation), 'validation_failure');
          }
        } catch (error) {
          restore();
          return finish(
            mcpToolResult(
              toolError(
                'UNEXPECTED',
                'An unexpected error occurred. The working copy was restored.',
                {
                  index,
                  operation: step.operation,
                  message: error instanceof Error ? error.message : String(error),
                },
              ),
            ),
            'unexpected_failure',
          );
        }
      }
      return finish(
        mcpToolResult(
          toolSuccess({
            operation: 'changes',
            applied: parsed.changes.length,
            workingRevision: readWorkingRevision(context.store.getState()),
          }),
        ),
        'success',
      );
    }
    if (!isTemplateBuilderOperation(String(input.operation ?? ''))) {
      return finish(
        mcpToolResult(
          toolError('INVALID_INPUT', 'template_builder_apply requires a known operation.'),
        ),
        'validation_failure',
      );
    }
    const result = await runOperation(context, input.operation as TemplateBuilderOperation, input);
    if (context.signal?.aborted) {
      return finish(
        mcpToolResult(toolError('UNEXPECTED', 'The tool invocation was cancelled.')),
        'cancellation',
      );
    }
    const outcome = result.structuredContent.ok
      ? 'success'
      : result.structuredContent.error.code === 'UNEXPECTED'
      ? 'unexpected_failure'
      : 'validation_failure';
    return finish(result, outcome);
  } catch (error) {
    return finish(
      mcpToolResult(
        toolError('UNEXPECTED', 'An unexpected error occurred.', {
          message: error instanceof Error ? error.message : String(error),
        }),
      ),
      'unexpected_failure',
    );
  }
}
